import {useRef} from 'react';

import {useMotionTemplate, useScroll, useTransform} from 'motion/react';

/**
 * A custom hook that handles background size animation for timeline items based on scroll progress.
 * This hook is designed to animate the vertical size of a background element using Framer Motion's `useScroll`
 * and transformation utilities. It creates a `ref` to be attached to the DOM element whose scroll visibility should be tracked.
 * As the user scrolls, the background grows from 0% to 100% height, enabling smooth vertical reveal effects.
 * The resulting `bgRef` should be attached to the DOM node that needs scroll tracking, and `bgSize` can be used to animate style properties.
 *
 * @returns An object containing a `ref` to track the DOM node (`bgRef`) and a `bgSize` string for dynamic background sizing.
 *
 * @example
 * ```tsx
 * const { bgRef, bgSize } = useTimelineItem();
 * return <div ref={bgRef} style={{ backgroundSize: bgSize }} />;
 * ```
 */
export const useSequenceItem = () => {
    const bgRef = useRef<HTMLDivElement>(null);
    const {scrollYProgress} = useScroll({
        offset: ['0 0.8', '1 0.8'],
        target: bgRef
    });
    const bgHeight = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
    const bgSize = useMotionTemplate`100% ${bgHeight}`;

    return {
        bgRef,
        bgSize
    };
};